import React from 'react';
import { MapPin, Navigation } from 'lucide-react';

/**
 * MapLink - Clickable address that opens in Google Maps
 * 
 * Usage:
 *   <MapLink address="123 Main St" city="Mooresville" state="NC" zipCode="28117" />
 *   <MapLink fullAddress="123 Main St, Mooresville, NC 28117" />
 *   <MapLink address={customer.address} city={customer.city} state={customer.state} zipCode={customer.zipCode} />
 */
const MapLink = ({ 
  address, 
  city, 
  state, 
  zipCode, 
  fullAddress,
  showIcon = true,
  iconPosition = 'left', // 'left' | 'right' | 'only'
  className = '',
  iconSize = 14,
  truncate = false,
  children
}) => {
  // Build full address from parts or use provided fullAddress
  const buildAddress = () => {
    if (fullAddress) return fullAddress;
    
    const parts = [];
    if (address) parts.push(address);
    
    const cityStateZip = [];
    if (city) cityStateZip.push(city);
    if (state) cityStateZip.push(state);
    if (zipCode) cityStateZip.push(zipCode);
    
    if (cityStateZip.length > 0) {
      parts.push(cityStateZip.join(', '));
    }
    
    return parts.join(', ');
  };

  const displayAddress = buildAddress();
  
  // Don't render anything if no address
  if (!displayAddress || displayAddress.trim() === '') {
    return null;
  }

  const mapUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(displayAddress)}`;

  const handleClick = (e) => {
    e.stopPropagation(); // Prevent triggering parent click handlers (like card clicks)
  };

  // Display text - either children, address, or just icon
  const displayText = children || displayAddress;

  return (
    <a
      href={mapUrl}
      target="_blank"
      rel="noopener noreferrer"
      onClick={handleClick}
      className={`inline-flex items-center gap-1.5 text-gray-600 hover:text-primary-600 transition-colors group ${className}`}
      title={`Open in Google Maps: ${displayAddress}`}
    >
      {showIcon && iconPosition === 'left' && (
        <MapPin 
          size={iconSize} 
          className="flex-shrink-0 text-gray-400 group-hover:text-primary-500 transition-colors" 
        />
      )}
      
      {iconPosition !== 'only' && (
        <span className={truncate ? 'truncate' : ''}>
          {displayText}
        </span>
      )}
      
      {showIcon && iconPosition === 'right' && (
        <Navigation 
          size={iconSize} 
          className="flex-shrink-0 text-gray-400 group-hover:text-primary-500 transition-colors" 
        />
      )}
      
      {iconPosition === 'only' && (
        <MapPin 
          size={iconSize} 
          className="flex-shrink-0 text-gray-400 group-hover:text-primary-500 transition-colors" 
        />
      )}
    </a>
  );
};

/**
 * MapButton - Standalone button to open maps (for action bars)
 */
export const MapButton = ({ 
  address, 
  city, 
  state, 
  zipCode, 
  fullAddress,
  size = 'sm',
  variant = 'ghost' // 'ghost' | 'outline'
}) => {
  const buildAddress = () => {
    if (fullAddress) return fullAddress;
    
    const parts = [];
    if (address) parts.push(address);
    
    const cityStateZip = [];
    if (city) cityStateZip.push(city);
    if (state) cityStateZip.push(state);
    if (zipCode) cityStateZip.push(zipCode);
    
    if (cityStateZip.length > 0) {
      parts.push(cityStateZip.join(', '));
    }
    
    return parts.join(', ');
  };

  const displayAddress = buildAddress();
  
  if (!displayAddress || displayAddress.trim() === '') {
    return null;
  }

  const mapUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(displayAddress)}`;

  const sizeClasses = {
    sm: 'p-1.5',
    md: 'p-2',
    lg: 'p-2.5'
  };

  const variantClasses = {
    ghost: 'text-gray-500 hover:text-primary-600 hover:bg-primary-50',
    outline: 'text-gray-600 hover:text-primary-600 border border-gray-200 hover:border-primary-300 hover:bg-primary-50'
  };

  const iconSizes = {
    sm: 14,
    md: 16,
    lg: 18
  };

  return (
    <a
      href={mapUrl}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      className={`inline-flex items-center justify-center rounded-lg transition-colors ${sizeClasses[size]} ${variantClasses[variant]}`}
      title={`Open in Google Maps: ${displayAddress}`}
    >
      <Navigation size={iconSizes[size]} />
    </a>
  );
};

export default MapLink;